import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/**
 * Ties a field's error message to the field itself, so a screen reader reads
 * "invalid: This field is required" on landing there rather than nothing.
 *
 * The phila inputs render the message but do not associate it: there is no
 * `aria-invalid` on the control and no id on the message to point at. Done
 * against the DOM because the two components differ: Textbox forwards unknown
 * attributes to its `<input>`, Dropdown keeps them on its wrapper. Watched with
 * a MutationObserver because the message comes from `<Field>`'s slot scope, so
 * a change re-renders Field and never updates the wrapper this belongs to.
 *
 * @param control  ref to the phila component; its `$el` is the field's wrapper
 */
export function useFieldErrors(control: Ref<{ $el?: HTMLElement } | null>) {
  let observer: MutationObserver | null = null
  /** Set by us, so we only ever clear our own. */
  let describedBy: string | null = null

  function sync() {
    const root = control.value?.$el
    if (!root || typeof root.querySelector !== 'function') return

    const field = root.querySelector('input, select, textarea')
    if (!field) return

    const message = root.querySelector<HTMLElement>('.input-error-msg')

    if (message) {
      if (!message.id) message.id = `${field.id || 'field'}-error`
      describedBy = message.id
      field.setAttribute('aria-invalid', 'true')
      field.setAttribute('aria-describedby', message.id)
      return
    }

    field.removeAttribute('aria-invalid')
    if (describedBy && field.getAttribute('aria-describedby') === describedBy) {
      field.removeAttribute('aria-describedby')
    }
    describedBy = null
  }

  onMounted(() => {
    const root = control.value?.$el
    if (!root || typeof root.querySelector !== 'function') return

    sync()

    // childList only. Writing the id and the aria-* attributes is an attribute
    // change, so not observing those is what keeps this from retriggering itself.
    observer = new MutationObserver(sync)
    observer.observe(root, { childList: true, subtree: true })
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
  })
}
